import useDidUpdateEffect from '@src/hooks/use-did-update-effect';
import { getDomainConfigValue } from '@src/model/config/helpers';
import { useEffect } from 'react';
import { useLocation } from 'react-router';

// Extend the Window interface for Comscore
declare global {
    interface Window {
        _comscore?: Record<string, unknown>[];
        COMSCORE?: {
            beacon: (params: Record<string, string | number>) => void;
        };
    }
}

export default function ComscoreTracker() {
    const location = useLocation();
    const comscoreConfig = getDomainConfigValue('comscore');

    useEffect(() => {
        if (!comscoreConfig?.clientId) return;
        // Initialize Comscore tracking
        initializeComscore(comscoreConfig.clientId, comscoreConfig.enableDebug);
    }, [comscoreConfig]);

    useDidUpdateEffect(() => {
        if (!comscoreConfig?.clientId) return;
        const timer = setTimeout(() => {
            trackComscorePageView(comscoreConfig.clientId, comscoreConfig.enableDebug);
        }, 1000);

        return () => clearTimeout(timer);
    }, [location.pathname]);

    return null;
}

function initializeComscore(clientId: string, enableDebug = false) {
    // Initialize _comscore array if it doesn't exist
    window._comscore = window._comscore || [];

    const userId = localStorage.getItem('userId');
    const comscoreUserId = userId || localStorage.getItem('comscoreUserId') || `anon-${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem('comscoreUserId', comscoreUserId);

    // Push initial configuration
    window._comscore.push({
        c1: '2',
        c2: clientId,
        cs_fpid: comscoreUserId,
        cs_fpit: userId ? 'li' : 'lo', // 'li' for logged-in, 'lo' for logged-out
        cs_fpdm: '*null',
        cs_fpdt: '*null',
        options: {
            enableFirstPartyCookie: true,
        },
    });

    // Load the Comscore script if not already loaded
    const scriptId = 'comscore-beacon-script';
    if (!document.getElementById(scriptId)) {
        const script = document.createElement('script');
        script.id = scriptId;
        script.async = true;
        script.src = `https://sb.scorecardresearch.com/cs/${clientId}/beacon.js`;

        // Insert before the first script tag
        const firstScript = document.getElementsByTagName('script')[0];
        if (firstScript?.parentNode) firstScript.parentNode.insertBefore(script, firstScript);
        else document.head.appendChild(script);

        if (enableDebug) console.log('[ComscoreTracker] Script loaded:', { clientId, comscoreUserId });
    }

    if (!document.getElementById('comscore-noscript')) {
        const noscript = document.createElement('noscript');
        noscript.id = 'comscore-noscript';
        noscript.innerHTML = `<img src="https://sb.scorecardresearch.com/p?c1=2&c2=${clientId}">`;
        document.body.appendChild(noscript);
    }
}

function trackComscorePageView(clientId: string, enableDebug = false) {
    if (typeof window.COMSCORE?.beacon !== 'function') return console.warn('[ComscoreTracker] COMSCORE.beacon is not defined');

    const comscoreParams = {
        c1: '2',
        c2: clientId,
    };

    if (enableDebug) console.log('[ComscoreTracker] Sending Comscore Beacon:', comscoreParams);
    window.COMSCORE.beacon(comscoreParams);
}
